import { useCallback, useEffect, useState } from 'react';

import {
  useCurrentProjectId,
  useCurrentToolId,
  useUser,
} from '@adsk/alloy-provider';

export const RECENTS_DEFAULT_MAX_LENGTH = 5;
export const RECENTS_DEFAULT_MIN_TO_SHOW = 3;
// Ensures unique React keys when a recent item also appears in its original group.
export const RECENTS_ID_PREFIX = 'recents:';
// Shared for cross-component notification so that other components get notified
// and if using the same key update their list of recents
export const RECENTS_UPDATED_EVENT = 'members-recents-updated';

export type RecentRecord = {
  id: string;
  type: string;
};

export function buildStorageKey(
  userId: string,
  projectId: string,
  toolId: string,
  fieldId: string | undefined,
): string {
  return ['recents', projectId, userId, toolId, fieldId]
    .filter(Boolean)
    .join('_');
}

/** Guards against corrupted or tampered localStorage data by validating item shape before use. */
function isRecentRecord(item: unknown): item is RecentRecord {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as RecentRecord).id === 'string' &&
    typeof (item as RecentRecord).type === 'string'
  );
}

export function readFromStorage(key: string): RecentRecord[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[]).filter(isRecentRecord);
  } catch {
    return [];
  }
}

export function writeToStorage(key: string, records: RecentRecord[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(records));
  } catch {
    // Ignore failures (private browsing, quota exceeded)
  }
}

export function addRecord(
  existing: RecentRecord[],
  newRecord: RecentRecord,
): RecentRecord[] {
  const withoutDuplicates = existing.filter(
    (r) => !(r.id === newRecord.id && r.type === newRecord.type),
  );
  const withNew = [newRecord, ...withoutDuplicates];
  const countByType: Record<string, number> = {};

  return withNew.filter((r) => {
    countByType[r.type] = (countByType[r.type] ?? 0) + 1;
    return countByType[r.type] <= RECENTS_DEFAULT_MAX_LENGTH;
  });
}

export const useRecentsPersistence = (fieldId?: string) => {
  const { user } = useUser();
  const projectId = useCurrentProjectId();
  const toolId = useCurrentToolId();

  const userId = user?.autodeskId;

  // Defer all I/O until userId/projectId/toolId are all available — otherwise the
  // hook would write a partial key on initial render (e.g. recents_userId_fieldId)
  // and orphan those entries once the full context resolves.
  const storageKey =
    userId && projectId && toolId
      ? buildStorageKey(userId, projectId, toolId, fieldId)
      : undefined;

  const [recents, setRecents] = useState<RecentRecord[]>([]);

  useEffect(() => {
    if (!storageKey) {
      setRecents([]);
      return undefined;
    }

    const sync = () => {
      const next = readFromStorage(storageKey);
      setRecents((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
      );
    };

    sync();

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string }>).detail;
      if (detail?.key === storageKey) sync();
    };
    globalThis.addEventListener(RECENTS_UPDATED_EVENT, handler);
    return () => globalThis.removeEventListener(RECENTS_UPDATED_EVENT, handler);
  }, [storageKey]);

  const addToRecents = useCallback(
    (item: RecentRecord) => {
      if (!storageKey) return;
      const record: RecentRecord = { id: item.id, type: item.type };
      setRecents((prev) => {
        const updated = addRecord(prev, record);
        writeToStorage(storageKey, updated);
        // Notify peers from inside the updater so they read the freshly-written
        // value when their listener fires synchronously.
        globalThis.dispatchEvent(
          new CustomEvent(RECENTS_UPDATED_EVENT, {
            detail: { key: storageKey },
          }),
        );
        return updated;
      });
    },
    [storageKey],
  );

  return { recents: storageKey ? recents : [], addToRecents };
};
